import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ocFetch } from "@/lib/oc-api";

export function OcAcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [status, setStatus] = useState<"loading" | "valid" | "invalid" | "done">("loading");
  const [email, setEmail] = useState("");
  const [invalidReason, setInvalidReason] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setInvalidReason("This invitation link is missing its token.");
      return;
    }
    ocFetch<{ valid: boolean; email?: string; error?: string }>(
      `/tenant/auth/invite?token=${encodeURIComponent(token)}`
    )
      .then((res) => {
        if (res.valid && res.email) {
          setEmail(res.email);
          setStatus("valid");
        } else {
          setInvalidReason(res.error ?? "This invitation link is no longer valid.");
          setStatus("invalid");
        }
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "";
        // ocFetch throws on non-2xx; surface the server's message when present.
        const m = msg.match(/\d{3}:\s*(.*)$/);
        let parsed = "";
        try { parsed = m ? (JSON.parse(m[1]!).error as string) : ""; } catch { /* ignore */ }
        setInvalidReason(parsed || "This invitation link is no longer valid.");
        setStatus("invalid");
      });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await ocFetch("/tenant/auth/accept-invite", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      setStatus("done");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to set password";
      const m = msg.match(/\d{3}:\s*(.*)$/);
      let parsed = "";
      try { parsed = m ? (JSON.parse(m[1]!).error as string) : ""; } catch { /* ignore */ }
      setError(parsed || "Failed to set your password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-oc-teal-500 focus:border-transparent";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 [color-scheme:light]">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <svg viewBox="0 0 48 54" fill="none" className="w-9 h-9 flex-shrink-0" aria-hidden="true">
              <path d="M24 2 L44 10 L44 26 C44 37 34 45 24 50 C14 45 4 37 4 26 L4 10 Z" fill="none" stroke="#00D4FF" strokeWidth="2.5" />
              <path d="M13 26 L20 33 L35 18" stroke="#00D4FF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="text-left">
              <div className="text-base font-bold text-slate-900 leading-tight">OnboardConnect</div>
              <div className="text-[10px] font-medium text-oc-teal-600 tracking-widest uppercase leading-tight">for Slate CRM</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Set your password</h1>
          <p className="mt-1 text-sm text-slate-500">Activate your OnboardConnect account</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          {status === "loading" && (
            <p className="text-sm text-slate-500 text-center py-4">Validating your invitation…</p>
          )}

          {status === "invalid" && (
            <div className="flex flex-col gap-4">
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                {invalidReason}
              </div>
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="w-full rounded-lg bg-oc-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-oc-teal-700 transition-colors"
              >
                Go to sign in
              </button>
            </div>
          )}

          {status === "done" && (
            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700 text-center">
              Password set. Redirecting you to sign in…
            </div>
          )}

          {status === "valid" && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700">Email</label>
                <input type="email" value={email} readOnly className={`${inputCls} opacity-60 cursor-not-allowed`} />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700">New password</label>
                <input
                  type="password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="At least 8 characters"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700">Confirm password</label>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={inputCls}
                  placeholder="Re-enter your password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-oc-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-oc-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? "Setting password…" : "Set password & activate"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
